<template>
  <div class="subscription-success-view">
    <v-row justify="center" class="mt-5">
      <v-col cols="12" sm="8" md="6" lg="5">
        <transition name="fade" mode="out-in">
          <v-card
            v-if="working"
            class="pa-8 text-center"
            outlined
            key="working"
          >
            <v-progress-circular
              color="blue darken-1"
              indeterminate
              class="mx-auto my-6"
            />
          </v-card>
          <v-card v-else-if="error" class="pa-8 text-left" outlined key="error">
            <h1 class="display-2 font-weight-bold mb-4">Thank You</h1>
            <p class="headline"
              >You've successfully signed up for the Minimart Basic Plan! An
              email receipt will be sent within a few minutes.
            </p>
            <v-alert type="info" text
              >Unfortunately there was an error setting up your store. Please
              contact support at
              <a href="mailto:hi@getminimart.com">hi@getminimart.com</a>
              so we can resolve the issue quickly</v-alert
            >
            <p class="title">We apologize for the inconvenience.</p>
            <p class="title font-italic">Team Minimart</p>
          </v-card>
          <v-card v-else class="pa-8" outlined key="success">
            <h1 class="display-2 font-weight-bold mb-4">Thank You</h1>
            <p class="headline"
              >You've successfully signed up for the Minimart Basic Plan! An
              email receipt will be sent within a few minutes.
            </p>
            <v-divider class="py-4" />
            <transition name="fade" mode="out-in">
              <div v-if="accountCreated" key="done-msg">
                <h2 class="display-1">Your Account Is Ready!</h2>
                <p class="title"
                  >Follow the link to your Minimart Owner dashboard login.
                </p>
                <v-btn
                  large
                  color="blue darken-1"
                  depressed
                  class="white--text mb-6"
                  :href="adminUrl || account.Domain"
                  >Owner Login</v-btn
                >
              </div>
              <div v-else key="create-form">
                <h2 class="display-1">One Final Step</h2>
                <p class="title">Setup your Owner Account for login</p>
                <v-form
                  ref="owner-form"
                  v-model="isOwnerFormValid"
                  @submit.prevent="maybeActivateAccount"
                >
                  <v-text-field
                    class="mm-input pb-3"
                    :value="email"
                    readonly
                    solo
                    flat
                    dense
                    label="Admin Email"
                    persistent-hint
                    hint="This email will be used for your Admin login and can be changed after your storefront is activated"
                  />
                  <set-password-field
                    @input="password = $event"
                    placeholder="Owner Password"
                    :disabled="activating"
                  />

                  <v-btn
                    v-if="!accountCreated"
                    large
                    color="blue darken-1"
                    depressed
                    class="white--text mb-6"
                    @click="maybeActivateAccount"
                    :disabled="!isOwnerFormValid || activating"
                    :loading="activating"
                    type="submit"
                    >Create My Account</v-btn
                  >

                  <v-alert type="info" v-if="activationError">{{
                    activationError
                  }}</v-alert>
                </v-form>
              </div>
            </transition>
          </v-card>
        </transition>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Lambda from '@/services/Lambda'
import SetPasswordField from '../components/SetPasswordField.vue'
import _get from 'lodash/get'

export default {
  name: 'SubscriptionSuccessView',
  components: { SetPasswordField },
  async created() {
    if (this.$route.query.session_id) {
      this.working = true
      try {
        await this.getStripeSession()
        await this.markAccountSubscribed()
      } catch (error) {
        this.error = 'Could not subscribe your account.'
      } finally {
        this.working = false
      }
    }
  },
  data() {
    return {
      working: false,
      password: '',
      error: false,
      isOwnerFormValid: false,
      accountCreated: false,
      activating: false,
      activationError: false,
      adminUrl: false,
      account: {},
    }
  },
  methods: {
    async getStripeSession() {
      const {
        data: { customer, session },
      } = await Lambda.post('retrieve_stripe_session', {
        sessionId: this.$route.query.session_id,
      })
      this.email = await _get(customer, 'email', '')
      this.shopRecordId = await _get(session, 'metadata.shopRecordId', false)
    }, // getStripeSession

    async markAccountSubscribed() {
      const { data: account } = await Lambda.post('mark_account_subscribed', {
        shopRecordId: this.shopRecordId,
        email: this.email,
      })
      this.account = account
    }, // markAccountSubscribed

    async maybeActivateAccount() {
      this.activating = true

      let { shopRecordId, password } = this
      let data = {
        shopBaseId: this.account.shopBaseId,
        shopRecordId,
        email: this.email,
        password,
        fields: {}, // airtable fields to update
      }
      try {
        let { data: adminUrl } = await Lambda.post('activate_shop', data)

        this.accountCreated = true
        this.adminUrl = adminUrl
      } catch (error) {
        this.activationError =
          error.response.data || 'Failed to activate your account'
      } finally {
        this.activating = false
      }
    }, // maybeActivateAccount
  },
}
</script>

<style lang="scss">
.subscription-success-view {
  .v-text-field {
    font-weight: normal;
    font-size: 1.5rem;
    line-height: 1.3;
    .v-messages__message {
      font-size: 0.9rem;
      line-height: 1.3;
    }
  }
}
</style>
